import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ActionPerformed, PushNotifications, Token } from '@capacitor/push-notifications';
import { Subject, firstValueFrom, of } from 'rxjs';
import { catchError, filter, take, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { NotificationLink, UVNotification } from '../models/notification';
import { ApiService } from './api.service';
import { AppStateService } from './app-state.service';
import { NotificationApiService } from './notification-api.service';
import { NotificationLinkService } from './notification-link.service';
import { PlatformService } from './platform.service';
import { ServiceEntitlementService } from './service-entitlement.service';
import { AuthState } from '../../store/auth/auth.reducer';
import * as AuthSelectors from '../../store/auth/auth.selectors';

// Must match the Push:AndroidChannelId server setting.
const ANDROID_CHANNEL_ID = 'urbanhub_alerts';
const ANDROID_IMPORTANCE_HIGH = 4;
const ANDROID_VISIBILITY_PUBLIC = 1;
const UNREGISTER_TIMEOUT_MS = 3000;
const REGISTER_TIMEOUT_MS = 10000;

export type PushEnableResult = 'enabled' | 'denied' | 'failed';

/**
 * Native push: device registration with the backend and the tap that opens the
 * alert's page. The OS permission is only ever asked from the profile toggle;
 * whether a push is sent at all is the user's channel preference on the server.
 */
@Injectable({ providedIn: 'root' })
export class PushService {
  private readonly platform = inject(PlatformService);
  private readonly apiService = inject(ApiService);
  private readonly notificationApi = inject(NotificationApiService);
  private readonly linkService = inject(NotificationLinkService);
  private readonly router = inject(Router);
  private readonly store = inject(Store<AuthState>);
  private readonly zone = inject(NgZone);
  private readonly appState = inject(AppStateService);
  private readonly entitlement = inject(ServiceEntitlementService);

  private listening = false;
  /** Whether the backend accepted the token of the latest OS registration. */
  private readonly registrationOutcome = new Subject<boolean>();
  /** The token the backend holds for this session; null until it accepted one. */
  private registeredToken: string | null = null;
  /** A tapped push waiting for the shell: the alert's link, judged once the shell is ready; the bell when it has none. */
  private pendingTap: NotificationLink | 'bell' | null = null;
  private waitingForShell = false;

  /**
   * App start, signed in or not. The plugin keeps a tap until a listener takes
   * it: listening only once signed in handed a tap made while logged out to
   * whoever signed in next.
   */
  public init(): void {
    if (this.platform.isNative) {
      this.listen();
    }
  }

  /** Session active (login or restore). Re-registers silently, never prompts. */
  public start(): void {
    if (!this.platform.isNative) {
      return;
    }

    this.listen();
    void this.isGranted().then(granted => granted ? this.register() : undefined);
  }

  public async isGranted(): Promise<boolean> {
    if (!this.platform.isNative) {
      return false;
    }

    try {
      return (await PushNotifications.checkPermissions()).receive === 'granted';
    } catch (error) {
      console.error('Push permission check failed', error);
      return false;
    }
  }

  /** Asks the OS permission, then registers the phone with the backend. */
  public async enable(): Promise<PushEnableResult> {
    if (!this.platform.isNative) {
      return 'failed';
    }

    this.listen();

    try {
      // Once the user has refused for good, the request returns 'denied'
      // without showing anything: only the system settings can undo it.
      let status = await PushNotifications.checkPermissions();
      if (status.receive !== 'granted') {
        status = await PushNotifications.requestPermissions();
      }
      if (status.receive !== 'granted') {
        return 'denied';
      }
    } catch (error) {
      console.error('Push permission request failed', error);
      return 'failed';
    }

    return (await this.register()) ? 'enabled' : 'failed';
  }

  /** Before logout, while the bearer still authorizes the call. Never rejects. */
  public async unregisterDevice(): Promise<void> {
    if (!this.platform.isNative || !this.registeredToken) {
      return;
    }

    await firstValueFrom(this.apiService.unregisterPushDevice({ Value: this.registeredToken }).pipe(
      timeout(UNREGISTER_TIMEOUT_MS),
      catchError(() => of(false)),
    ));
  }

  /**
   * Session over, whatever ended it. FCM drops the token, so a phone left
   * logged out receives nothing even when the backend call above never ran;
   * the backend deletes the row at the next send.
   */
  public stop(): void {
    this.registeredToken = null;
    this.pendingTap = null;

    if (this.platform.isNative) {
      PushNotifications.unregister().catch(error => console.error('Push unregister failed', error));
    }
  }

  // The token arrives through the 'registration' event and the backend's
  // answer through onToken: the outcome is awaited from there.
  private async register(): Promise<boolean> {
    const outcome: Promise<boolean> = firstValueFrom(this.registrationOutcome.pipe(
      timeout(REGISTER_TIMEOUT_MS),
      catchError(() => of(false)),
    ));

    try {
      if (this.platform.platform === 'android') {
        await PushNotifications.createChannel({
          id: ANDROID_CHANNEL_ID,
          name: 'UrbanHub',
          importance: ANDROID_IMPORTANCE_HIGH,
          visibility: ANDROID_VISIBILITY_PUBLIC,
        });
      }
      await PushNotifications.register();
    } catch (error) {
      console.error('Push registration failed', error);
      return false;
    }

    return outcome;
  }

  // Plugin events arrive outside the Angular zone.
  private listen(): void {
    if (this.listening) {
      return;
    }
    this.listening = true;

    void PushNotifications.addListener('registration', (token: Token) =>
      this.zone.run(() => this.onToken(token.value)));
    void PushNotifications.addListener('registrationError', error => this.zone.run(() => {
      console.error('Push registration failed', error);
      this.registrationOutcome.next(false);
    }));
    void PushNotifications.addListener('pushNotificationReceived', () =>
      this.zone.run(() => this.refreshBell()));
    void PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) =>
      this.zone.run(() => this.onTap(action)));

    this.platform.appState$.subscribe(active => {
      if (active) {
        this.refreshBell();
      }
    });

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.openPendingTap(event.urlAfterRedirects));
  }

  private onToken(value: string): void {
    if (!value || value === this.registeredToken) {
      this.registrationOutcome.next(value === this.registeredToken);
      return;
    }

    this.whenAuthenticated(() => {
      this.apiService.registerPushDevice({
        Platform: this.platform.platform === 'ios' ? 'ios' : 'android',
        Kind: 'token',
        Value: value,
        AppVersion: environment.appVersion,
      }).subscribe(accepted => {
        if (accepted) {
          this.registeredToken = value;
        }
        this.registrationOutcome.next(accepted);
      });
    });
  }

  // A restored session is in the store before the app starts, so a tap with
  // nobody signed in belongs to a session that is over: it is dropped.
  private onTap(action: ActionPerformed): void {
    this.whenAuthenticated(() => {
      this.pendingTap = this.linkService.fromData(action?.notification?.data) ?? 'bell';
      this.notificationApi.skipLiveArrivals();
      this.refreshBell();
      this.openPendingTap(this.router.url);
    });
  }

  // A tap that starts the app arrives before the session is restored or the
  // user has logged in. It opens once the shell has loaded its data: during
  // that load a dialog lands on a half-drawn page, and NavigationEnd even
  // precedes the shell's ngOnInit, which stores the page's navigation info.
  // Whether the link opens a page is judged only then: 'edge/device' depends
  // on the active services and the grants, both empty on a cold start.
  private openPendingTap(url: string): void {
    if (!this.pendingTap || this.waitingForShell || !url.startsWith('/main/')) {
      return;
    }
    this.waitingForShell = true;

    this.appState.whenSettled().subscribe(() => this.zone.run(() => {
      this.waitingForShell = false;
      this.whenAuthenticated(() => {
        const tap: NotificationLink | 'bell' | null = this.pendingTap;
        this.pendingTap = null;
        if (!tap) {
          return;
        }
        this.entitlement.ensureLoaded().pipe(take(1)).subscribe(() => {
          if (tap !== 'bell' && this.linkService.canOpen(tap)) {
            this.linkService.open(tap);
            this.markTappedRead(tap);
          } else {
            this.notificationApi.requestBellOpen();
          }
        });
      });
    }));
  }

  // The push carries no notification id: the newest unread alert of the same
  // page stands for the one tapped.
  private markTappedRead(link: NotificationLink): void {
    this.notificationApi.getUnreadNotifications().subscribe((notifications: UVNotification[]) => {
      const tapped: UVNotification | undefined = notifications
        .filter(n => this.linkService.matches(n.Link, link))
        .sort((a, b) => (b.Created ?? 0) - (a.Created ?? 0))[0];

      if (tapped) {
        this.notificationApi.readNotification(tapped.Id)
          .subscribe(() => this.notificationApi.triggerRefresh());
      }
    });
  }

  private refreshBell(): void {
    this.whenAuthenticated(() => this.notificationApi.triggerRefresh());
  }

  private whenAuthenticated(action: () => void): void {
    this.store.select(AuthSelectors.isAuth)
      .pipe(take(1))
      .subscribe(isAuthenticated => {
        if (isAuthenticated) {
          action();
        }
      });
  }
}
